import { useEffect, useState } from 'react';
import { type AuthResult } from '@aiscoreboard/contracts';
import { AvatarCropper } from './AvatarCropper';
import { CountryPicker } from './CountryPicker';
import { login, registerPlayer, uploadAvatar } from '../lib/accounts';
import { useT } from '../lib/i18n';
import { IMAGE_ACCEPT, looksLikeImage } from '../lib/images';

type Tab = 'in' | 'new';

/** How big a photo the browser is asked to decode before cropping it. */
const SOURCE_MAX_BYTES = 25 * 1024 * 1024;

interface Props {
  title: string;
  onDone: (auth: AuthResult) => void;
  onCancel: () => void;
}

/**
 * Two things, and only two: sign in, or make an account.
 *
 * There used to be a third tab holding a pairing QR code, which was the same
 * code already on display behind this panel — the seat card shows one per
 * seat. Two identical codes on one screen is not a choice, it is a question
 * about which one is real.
 */
export function SignInPanel({ title, onDone, onCancel }: Props) {
  const t = useT();
  const [tab, setTab] = useState<Tab>('in');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [country, setCountry] = useState('');
  const [clubName, setClubName] = useState('');
  const [clubCountry, setClubCountry] = useState('');
  const [clubCity, setClubCity] = useState('');
  const [avatar, setAvatar] = useState<File | null>(null);
  // The file straight off the disk. It is never uploaded — only the square
  // the player chooses out of it is — but it is kept so that adjusting the
  // crop starts from the whole photo again instead of from the last square.
  const [original, setOriginal] = useState<File | null>(null);
  const [cropping, setCropping] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);

  useEffect(() => {
    if (!avatar) {
      setPreview(null);
      return;
    }
    const url = URL.createObjectURL(avatar);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [avatar]);

  const run = async (work: () => Promise<AuthResult>): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      onDone(await work());
    } catch (e) {
      setError(e instanceof Error ? e.message : t('signin.failed'));
    } finally {
      setBusy(false);
    }
  };

  const submitRegistration = (): void => {
    void run(async () => {
      const auth = await registerPlayer({
        firstName,
        lastName,
        email,
        country,
        password,
        club: clubName ? { name: clubName, country: clubCountry, city: clubCity } : undefined,
      });
      // The avatar is a nice-to-have: a failed upload must not lose the
      // account that was just created.
      if (avatar) {
        try {
          // The password was typed on this very form a moment ago, which is
          // exactly the proof the upload asks for.
          auth.player = await uploadAvatar(auth.token, avatar, password);
        } catch {
          /* ignore */
        }
      }
      return auth;
    });
  };

  const pickAvatar = (file: File | null): void => {
    if (!file) {
      setAvatar(null);
      setOriginal(null);
      setCropping(null);
      return;
    }
    // Only the obviously-not-a-picture is turned away here. Which formats
    // this device can actually decode is a question for the cropper, which
    // has three ways of asking it and will convert a HEIC if it has to; a
    // camera roll photo handed over with no MIME type at all, or with the
    // made-up `image/jpg`, is a normal thing to be given.
    if (!looksLikeImage(file)) {
      setError(t('signin.avatarType'));
      return;
    }
    // The server's 2 MB limit applies to what is uploaded, and what is
    // uploaded is a 512px square this component makes — not the 6 MB portrait
    // a phone camera produced. Only the size the browser has to decode is
    // capped here, and generously.
    if (file.size > SOURCE_MAX_BYTES) {
      setError(t('signin.avatarSize'));
      return;
    }
    setError(null);
    // Nothing is kept until the square is chosen, so backing out of the
    // cropper leaves whatever avatar was already there alone.
    setOriginal(file);
    setCropping(file);
  };

  const passwordsMatch = password === confirmPassword;

  const registrationReady =
    firstName.trim() !== '' &&
    lastName.trim() !== '' &&
    // Checked properly by the server; this only stops the button being live
    // before there is anything worth sending.
    /.+@.+\..+/.test(email.trim()) &&
    country !== '' &&
    password !== '' &&
    confirmPassword !== '' &&
    passwordsMatch &&
    (clubName.trim() === '' || (clubCountry !== '' && clubCity.trim() !== ''));

  return (
    <div className="panel flex max-h-[calc(100dvh-2rem)] w-full max-w-md shrink-0 flex-col gap-4 overflow-y-auto">
      <div className="flex items-center gap-2">
        <h2 className="text-lg font-bold">{title}</h2>
        <button type="button" onClick={onCancel} className="btn-ghost ml-auto px-3 py-1 text-sm">
          {t('signin.close')}
        </button>
      </div>

      <div className="flex gap-1 rounded-full bg-white/[0.06] p-1">
        {(
          [
            ['in', 'signin.tabSignIn'],
            ['new', 'signin.tabNew'],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => {
              setTab(key);
              setError(null);
            }}
            className={[
              'flex-1 rounded-full px-2 py-2 text-sm font-semibold transition',
              tab === key ? 'bg-white text-hud-bg' : 'text-white/70 hover:bg-white/10',
            ].join(' ')}
          >
            {t(label)}
          </button>
        ))}
      </div>

      {tab === 'in' && (
        <div className="flex flex-col gap-3">
          <div>
            <span className="label">{t('signin.username')}</span>
            <input
              className="field font-mono"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              value={username}
              maxLength={80}
              placeholder="Alex#00000042"
              onChange={(e) => setUsername(e.target.value)}
            />
            <p className="mt-1 min-h-[2rem] text-xs text-white/40 max-sm:min-h-[3rem]">{t('signin.usernameHint')}</p>
          </div>
          <div>
            <span className="label">{t('signin.password')}</span>
            <input
              type="password"
              className="field"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void run(() => login(username, password));
              }}
            />
          </div>
          <button
            type="button"
            disabled={busy || username.trim() === '' || password === ''}
            onClick={() => void run(() => login(username, password))}
            className="btn bg-white text-hud-bg"
          >
            {busy ? t('signin.signingIn') : t('signin.signIn')}
          </button>
        </div>
      )}

      {tab === 'new' && (
        <div className="flex max-h-[60vh] flex-col gap-3 overflow-y-auto pr-1">
          <div className="flex gap-2">
            <div className="flex-1">
              <span className="label">{t('signin.firstName')}</span>
              <input
                className="field"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
              />
            </div>
            <div className="flex-1">
              <span className="label">{t('signin.lastName')}</span>
              <input
                className="field"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
              />
            </div>
          </div>

          <div>
            <span className="label">{t('signin.email')}</span>
            <input
              type="email"
              className="field"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <p className="mt-1 text-xs text-white/40">{t('signin.emailHint')}</p>
          </div>

          <CountryPicker value={country} onChange={setCountry} label={t('signin.nationality')} />

          <div>
            <span className="label">{t('signin.club')}</span>
            <input
              className="field"
              placeholder={t('signin.clubName')}
              value={clubName}
              onChange={(e) => setClubName(e.target.value)}
            />
          </div>

          {clubName.trim() !== '' && (
            <>
              <CountryPicker
                value={clubCountry}
                onChange={setClubCountry}
                label={t('signin.clubCountry')}
              />
              <div>
                <span className="label">{t('signin.clubCity')}</span>
                <input
                  className="field"
                  value={clubCity}
                  onChange={(e) => setClubCity(e.target.value)}
                />
              </div>
            </>
          )}

          <div>
            <span className="label">{t('signin.password')}</span>
            <input
              type="password"
              className="field"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <p className="mt-1 text-xs text-white/40">{t('signin.passwordHint')}</p>
          </div>

          <div>
            <span className="label">{t('signin.confirmPassword')}</span>
            <input
              type="password"
              className="field"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
            {/* Only complain once there is something to compare, so the
                warning does not appear while the second field is being typed
                for the first time. */}
            {confirmPassword !== '' && !passwordsMatch && (
              <p className="mt-1 text-xs text-ball-red">{t('signin.mismatch')}</p>
            )}
          </div>

          <div>
            <span className="label">{t('signin.avatar')}</span>
            <div className="flex items-center gap-3">
              {preview && (
                <img
                  src={preview}
                  alt=""
                  className="h-14 w-14 shrink-0 rounded-full object-cover ring-2 ring-white/20"
                />
              )}
              <input
                type="file"
                accept={IMAGE_ACCEPT}
                onChange={(e) => {
                  pickAvatar(e.target.files?.[0] ?? null);
                  // Let the same file be picked again after a cancelled crop.
                  e.target.value = '';
                }}
                className="min-w-0 flex-1 text-sm text-white/70 file:mr-3 file:rounded-lg file:border-0
                           file:bg-white/10 file:px-3 file:py-2 file:text-white"
              />
            </div>
            {avatar && original && (
              <button
                type="button"
                onClick={() => setCropping(original)}
                className="mt-1 text-xs text-white/50 underline underline-offset-2"
              >
                {t('crop.again')}
              </button>
            )}
          </div>

          <button
            type="button"
            disabled={busy || !registrationReady}
            onClick={submitRegistration}
            className="btn bg-white text-hud-bg"
          >
            {busy ? t('signin.creating') : t('signin.create')}
          </button>
        </div>
      )}

      {error && <p className="text-sm text-ball-red">{error}</p>}

      {cropping && (
        <AvatarCropper
          file={cropping}
          onCancel={() => setCropping(null)}
          onCropped={(cropped) => {
            setAvatar(cropped);
            setCropping(null);
          }}
        />
      )}
    </div>
  );
}
